import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export const DISPLAY = { fontFamily: "'Sora', ui-sans-serif, sans-serif" };
export const BODY = { fontFamily: "'Manrope', ui-sans-serif, sans-serif" };
export const ACCENTS = { blue: "#3b82f6", cyan: "#22d3ee", violet: "#8b5cf6" };

export function useLawfirmFonts() {
  useEffect(() => {
    if (document.getElementById("lfm-fonts")) return;
    const l = document.createElement("link");
    l.id = "lfm-fonts"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap";
    document.head.appendChild(l);
  }, []);
}

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

export const Sec = ({ id, children, className = "", inner = "" }) => (
  <section id={id} className={`relative scroll-mt-20 px-5 sm:px-8 py-20 sm:py-28 ${className}`}>
    <div className={`max-w-7xl mx-auto ${inner}`}>{children}</div>
  </section>
);

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-300/90 ${className}`}>
    <span className="w-6 h-px bg-gradient-to-r from-cyan-300 to-transparent" />{children}
  </div>
);

export const H2 = ({ children, className = "" }) => (
  <h2 style={DISPLAY} className={`mt-4 text-[2rem] leading-[1.08] sm:text-5xl font-semibold tracking-[-0.03em] text-white ${className}`}>{children}</h2>
);

export const Sub = ({ children, className = "" }) => <p className={`mt-5 text-[15px] sm:text-[17px] leading-relaxed text-white/60 max-w-2xl ${className}`}>{children}</p>;

export const Glass = ({ children, className = "", ...p }) => (
  <div {...p} className={`rounded-2xl border border-white/[0.09] bg-white/[0.035] backdrop-blur-xl shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] ${className}`}>{children}</div>
);

export const WinBar = ({ title, right }) => (
  <div className="flex items-center gap-2 px-4 h-10 border-b border-white/[0.07] text-[11.5px] text-white/45">
    <span className="flex gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-white/15" /><i className="w-2.5 h-2.5 rounded-full bg-white/15" /><i className="w-2.5 h-2.5 rounded-full bg-white/15" /></span>
    <span className="ml-2 truncate">{title}</span><span className="ml-auto">{right}</span>
  </div>
);

export const DemoTag = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 bg-amber-300/[0.07] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/90 ${className}`} data-testid="lfm-demo-tag">
    <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />Product simulation · sample data
  </span>
);

const btnBase = "inline-flex items-center justify-center gap-2 rounded-full px-6 h-12 text-[14.5px] font-semibold transition-[transform,box-shadow,background-color,border-color] duration-300 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70";
const btnCls = {
  primary: `${btnBase} text-[#04121c] bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 shadow-[0_0_40px_-8px_rgba(56,189,248,0.75)] hover:shadow-[0_0_56px_-6px_rgba(56,189,248,0.95)] hover:-translate-y-0.5`,
  ghost: `${btnBase} text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/30`,
};

export const Btn = ({ to, onClick, variant = "primary", children, testid, className = "" }) =>
  to ? <Link to={to} data-testid={testid} className={`${btnCls[variant]} ${className}`}>{children}</Link>
    : <button type="button" onClick={onClick} data-testid={testid} className={`${btnCls[variant]} ${className}`}>{children}</button>;

export const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export const Feature = ({ icon: Icon, title, children }) => (
  <div className="group flex gap-3.5">
    <span className="mt-0.5 w-9 h-9 shrink-0 rounded-xl flex items-center justify-center border border-white/10 bg-gradient-to-br from-sky-400/15 to-violet-500/10 transition-colors group-hover:border-cyan-300/40"><Icon size={16} className="text-cyan-200" /></span>
    <div><div className="text-[14.5px] font-semibold text-white">{title}</div><p className="mt-1 text-[13.5px] leading-relaxed text-white/55">{children}</p></div>
  </div>
);

export const Closing = ({ children }) => (
  <Reveal className="mt-14 text-center"><p style={DISPLAY} className="text-xl sm:text-2xl font-medium tracking-tight bg-gradient-to-r from-white via-sky-200 to-violet-300 bg-clip-text text-transparent">{children}</p></Reveal>
);
